import { useState } from "react";

import { useAnalyzeBugCoverage } from "../api/bug-coverage.api.ts";
import { useTickets } from "../api/tickets.api.ts";
import { Badge, Button, Card, Page } from "../components/Page.tsx";

const COVERAGE_TONE = {
  yes: "success",
  partial: "warn",
  no: "danger",
} as const;

export function BugCoverage() {
  const bugs = useTickets({ type: "bug" });
  const analyze = useAnalyzeBugCoverage();
  const [selected, setSelected] = useState<string | null>(null);

  const selectedBug = (bugs.data?.tickets ?? []).find((t) => t.id === selected);

  return (
    <Page
      title="Bug coverage"
      description="Pick a bug ticket. Oracus will check whether existing tests cover the scenario and cite the evidence."
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[20rem_1fr]">
        <Card title="Bug tickets">
          {bugs.isLoading && <p className="text-sm text-zinc-500">Loading…</p>}
          {bugs.data?.tickets.length === 0 && (
            <p className="text-sm text-zinc-500">
              No bugs ingested. Upload Jira JSON first.
            </p>
          )}
          <ul className="space-y-1">
            {(bugs.data?.tickets ?? []).map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => setSelected(t.id)}
                  className={`block w-full rounded-md px-2.5 py-1.5 text-left text-sm transition ${
                    selected === t.id
                      ? "bg-zinc-900 text-white"
                      : "hover:bg-zinc-100"
                  }`}
                >
                  <span className="font-mono text-xs opacity-70">
                    {t.id}
                  </span>{" "}
                  {t.title}
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          {!selectedBug ? (
            <p className="text-sm text-zinc-500">Select a bug to analyze.</p>
          ) : (
            <>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-zinc-500">
                      {selectedBug.id}
                    </span>
                    {selectedBug.status && (
                      <Badge tone="neutral">{selectedBug.status}</Badge>
                    )}
                  </div>
                  <h2 className="mt-1 text-lg font-semibold">
                    {selectedBug.title}
                  </h2>
                  {selectedBug.description && (
                    <p className="mt-2 text-sm text-zinc-700 whitespace-pre-wrap">
                      {selectedBug.description}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => analyze.mutate(selectedBug.id)}
                  disabled={analyze.isPending}
                >
                  {analyze.isPending ? "Analyzing…" : "Analyze coverage"}
                </Button>
              </div>

              {analyze.error && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {String((analyze.error as Error).message)}
                </div>
              )}

              {analyze.data && (
                <div className="mt-6 space-y-4 border-t border-zinc-100 pt-4">
                  <div className="flex items-center gap-2">
                    <Badge tone={COVERAGE_TONE[analyze.data.covered]}>
                      coverage: {analyze.data.covered}
                    </Badge>
                    <span className="text-xs text-zinc-500">
                      {analyze.data.modelUsed}
                    </span>
                  </div>
                  <p className="text-sm">{analyze.data.reasoning}</p>
                  {analyze.data.supportingTests.length > 0 && (
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                        Supporting tests
                      </div>
                      <ul className="mt-2 space-y-2">
                        {analyze.data.supportingTests.map((t, idx) => (
                          <li
                            key={idx}
                            className="rounded-md bg-zinc-50 p-3 text-sm"
                          >
                            <div className="flex items-center gap-2">
                              <Badge
                                tone={
                                  t.relevance === "high"
                                    ? "success"
                                    : t.relevance === "medium"
                                      ? "info"
                                      : "neutral"
                                }
                              >
                                {t.relevance}
                              </Badge>
                              <code className="text-xs">{t.testId}</code>
                            </div>
                            <p className="mt-1 text-zinc-700">{t.why}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {analyze.data.gapDescription && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      <div className="font-medium">Gap</div>
                      <p className="mt-1">{analyze.data.gapDescription}</p>
                      {analyze.data.suggestedTestName && (
                        <p className="mt-2 text-amber-900">
                          Suggested test:{" "}
                          <code className="text-xs">
                            {analyze.data.suggestedTestName}
                          </code>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </Page>
  );
}
